// ============================================================================
// Protected Route - Route Guard with Hydration Awareness
// EduPath Africa v2.0 - Routing Architecture
// ============================================================================

import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore, useAuthReady } from '@/stores/authStore';
import { getDashboardForRole, canRoleAccessPath } from '@/lib/roleConfig';
import type { UserRole } from '@/lib/roleConfig';

// ============================================================================
// Loading Component
// ============================================================================

const AuthLoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// ============================================================================
// Access Denied Component
// ============================================================================

interface AccessDeniedProps {
  message?: string;
  redirectPath?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ 
  message = "You don't have permission to access this page.",
  redirectPath 
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center max-w-md px-4">
      <div className="text-6xl mb-4">🚫</div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
      <p className="text-gray-600 mb-6">{message}</p>
      {redirectPath && (
        <a 
          href={redirectPath}
          className="inline-block px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go to Dashboard
        </a>
      )}
    </div>
  </div>
);

// ============================================================================
// Protected Route Component
// ============================================================================

interface ProtectedRouteProps {
  /**
   * Specific roles allowed to access this route.
   * If not specified, any authenticated user can access.
   */
  allowedRoles?: UserRole[];
  
  /**
   * Whether authentication is required.
   * @default true
   */
  requireAuth?: boolean;
  
  /**
   * Custom redirect path when access is denied.
   * If not specified, redirects to role-appropriate dashboard.
   */
  accessDeniedRedirect?: string;
  
  /**
   * Children to render (for direct children pattern).
   * If not provided, renders <Outlet /> for nested routes.
   */
  children?: React.ReactNode;
  
  /**
   * Whether to show AccessDenied component instead of redirecting.
   * @default false
   */
  showAccessDenied?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  requireAuth = true,
  accessDeniedRedirect,
  children,
  showAccessDenied = false,
}) => {
  const location = useLocation();
  const isReady = useAuthReady();
  const { isAuthenticated, user } = useAuthStore();
  
  // ------------------------------------------------------------------
  // FALLBACK: Check localStorage directly if Zustand state seems wrong
  // This handles race conditions during navigation
  // ------------------------------------------------------------------
  const checkLocalStorageAuth = (): boolean => {
    try {
      const stored = localStorage.getItem('edupath-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.state?.isAuthenticated === true && !!parsed?.state?.accessToken;
      }
    } catch (e) {
      console.error('Failed to check localStorage auth:', e);
    }
    return false;
  };
  
  // Use localStorage as fallback if Zustand shows not authenticated
  const hasLocalStorageAuth = !isAuthenticated ? checkLocalStorageAuth() : false;
  const effectivelyAuthenticated = isAuthenticated || hasLocalStorageAuth;
  
  // Debug logging
  console.log('[ProtectedRoute]', {
    path: location.pathname,
    isReady,
    isAuthenticated,
    hasLocalStorageAuth,
    effectivelyAuthenticated,
    userRole: user?.role,
    allowedRoles,
    hasUser: !!user,
  });
  
  // ------------------------------------------------------------------
  // CRITICAL: Wait for hydration before making any decisions
  // ------------------------------------------------------------------
  if (!isReady) {
    console.log('[ProtectedRoute] Waiting for hydration...');
    return <AuthLoadingScreen />;
  }
  
  // ------------------------------------------------------------------
  // Authentication Check
  // ------------------------------------------------------------------
  if (requireAuth && !effectivelyAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    // Save the attempted URL for post-login redirect
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname + location.search }} 
        replace 
      />
    );
  }
  
  // If we have localStorage auth but no user in state, wait a bit
  // This gives Zustand time to rehydrate
  if (hasLocalStorageAuth && !user) {
    console.log('[ProtectedRoute] Have localStorage auth but no user, waiting...');
    return <AuthLoadingScreen />;
  }
  
  // ------------------------------------------------------------------
  // Role-Based Access Check
  // ------------------------------------------------------------------
  if (allowedRoles && user) {
    const hasAccess = allowedRoles.includes(user.role);
    
    if (!hasAccess) {
      console.log('[ProtectedRoute] Role mismatch:', user.role, 'not in', allowedRoles);
      const redirectPath = accessDeniedRedirect || getDashboardForRole(user.role);
      
      if (showAccessDenied) {
        return (
          <AccessDenied 
            message={`This page requires one of these roles: ${allowedRoles.join(', ')}`}
            redirectPath={redirectPath}
          />
        );
      }
      
      return <Navigate to={redirectPath} replace />;
    }
  }
  
  // ------------------------------------------------------------------
  // Path-Based Access Check (optional secondary check)
  // ------------------------------------------------------------------
  if (user && !canRoleAccessPath(user.role, location.pathname)) {
    console.log('[ProtectedRoute] Path access denied');
    const redirectPath = getDashboardForRole(user.role);
    return <Navigate to={redirectPath} replace />;
  }
  
  // ------------------------------------------------------------------
  // Render Content
  // ------------------------------------------------------------------
  // Use children if provided, otherwise use Outlet for nested routes
  return <>{children || <Outlet />}</>;
};

// ============================================================================
// Public Route Component (for auth pages)
// ============================================================================

interface PublicRouteProps {
  /**
   * Redirect authenticated users to their dashboard.
   * @default true
   */
  redirectAuthenticated?: boolean;
  
  /**
   * Custom redirect path for authenticated users.
   */
  redirectTo?: string;
  
  /**
   * Children to render.
   */
  children?: React.ReactNode;
}

export const PublicRoute: React.FC<PublicRouteProps> = ({
  redirectAuthenticated = true,
  redirectTo,
  children,
}) => {
  const isReady = useAuthReady();
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  
  // ------------------------------------------------------------------
  // Wait for hydration
  // ------------------------------------------------------------------
  if (!isReady) {
    return <AuthLoadingScreen />;
  }
  
  // ------------------------------------------------------------------
  // Redirect authenticated users
  // ------------------------------------------------------------------
  if (redirectAuthenticated && isAuthenticated && user) {
    // Check for a "from" state to redirect back to original page
    const from = (location.state as { from?: string })?.from;
    const destination = from || redirectTo || getDashboardForRole(user.role);
    
    return <Navigate to={destination} replace />;
  }
  
  return <>{children || <Outlet />}</>;
};

// ============================================================================
// Role-Specific Route Components
// ============================================================================

interface RoleRouteProps {
  children?: React.ReactNode;
}

export const LearnerRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['LEARNER']}>
    {children}
  </ProtectedRoute>
);

export const GuardianRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['GUARDIAN']}>
    {children}
  </ProtectedRoute>
);

export const TeacherRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['TEACHER', 'COUNSELOR']}>
    {children}
  </ProtectedRoute>
);

export const CounselorRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['COUNSELOR']}>
    {children}
  </ProtectedRoute>
);

export const SchoolAdminRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'ADMIN', 'SUPER_ADMIN']}>
    {children}
  </ProtectedRoute>
);

export const AdminRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
    {children}
  </ProtectedRoute>
);

export const SuperAdminRoute: React.FC<RoleRouteProps> = ({ children }) => (
  <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
    {children}
  </ProtectedRoute>
);

// ============================================================================
// HOC Pattern (for wrapping page components)
// ============================================================================

export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    allowedRoles?: UserRole[];
    requireAuth?: boolean;
  }
) {
  const WrappedComponent: React.FC<P> = (props) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
  
  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name || 'Component'})`;
  
  return WrappedComponent;
}

export default ProtectedRoute;
